import { useCallback } from 'react'
import { useLoginMutation, useLogoutMutation, useRefreshMutation } from '@/api/auth.api'
import { useAuthStore } from '@/store/auth.store'
import { authUserFromClaims, decodeJwt } from '@/utils/jwt'

export interface LoginResult {
  ok: boolean
  error?: string
}

/**
 * Central auth hook. Owns login, logout and silent re-auth, deriving the user
 * identity from the access-token claims (tap-api login returns tokens only).
 */
export function useAuth() {
  const accessToken = useAuthStore((s) => s.accessToken)
  const refreshToken = useAuthStore((s) => s.refreshToken)
  const user = useAuthStore((s) => s.user)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)

  const [loginMutation, loginState] = useLoginMutation()
  const [logoutMutation] = useLogoutMutation()
  const [refreshMutation] = useRefreshMutation()

  const login = useCallback(
    async (email: string, password: string): Promise<LoginResult> => {
      try {
        const tokens = await loginMutation({ email, password }).unwrap()
        const claims = decodeJwt(tokens.accessToken)
        if (!claims) return { ok: false, error: 'Received an invalid session token.' }
        useAuthStore
          .getState()
          .setSession(
            { accessToken: tokens.accessToken, refreshToken: tokens.refreshToken },
            authUserFromClaims(claims),
          )
        return { ok: true }
      } catch (error) {
        return { ok: false, error: extractErrorMessage(error) }
      }
    },
    [loginMutation],
  )

  const logout = useCallback(async (): Promise<void> => {
    const token = useAuthStore.getState().refreshToken
    // Best-effort server-side revocation; the local session is cleared regardless.
    if (token) {
      try {
        await logoutMutation({ refreshToken: token }).unwrap()
      } catch {
        // Ignore — we still clear the local session below.
      }
    }
    useAuthStore.getState().clearSession()
  }, [logoutMutation])

  /**
   * On startup: if a user + refresh token survived a reload but the in-memory
   * access token is gone, silently mint a new pair. Preserves enriched user
   * fields (name, tenantName, permissions) across the refresh.
   */
  const attemptSilentRefresh = useCallback(async (): Promise<boolean> => {
    const state = useAuthStore.getState()
    if (state.accessToken || !state.refreshToken || !state.user) return false
    try {
      const tokens = await refreshMutation({ refreshToken: state.refreshToken }).unwrap()
      const claims = decodeJwt(tokens.accessToken)
      if (!claims) {
        state.clearSession()
        return false
      }
      state.setSession(
        { accessToken: tokens.accessToken, refreshToken: tokens.refreshToken },
        authUserFromClaims(claims, {
          name: state.user.name,
          tenantName: state.user.tenantName,
          permissions: state.user.permissions,
        }),
      )
      return true
    } catch {
      state.clearSession()
      return false
    }
  }, [refreshMutation])

  return {
    accessToken,
    refreshToken,
    user,
    isAuthenticated,
    isLoggingIn: loginState.isLoading,
    login,
    logout,
    attemptSilentRefresh,
  }
}

/** Pull a human-readable message out of an RTK Query error. */
function extractErrorMessage(error: unknown): string {
  if (typeof error === 'object' && error !== null && 'data' in error) {
    const data = error.data
    if (typeof data === 'object' && data !== null && 'error' in data) {
      const inner = data.error
      if (typeof inner === 'object' && inner !== null && 'message' in inner) {
        const message = inner.message
        if (typeof message === 'string') return message
      }
    }
  }
  return 'Login failed. Please check your credentials and try again.'
}
